/**
 * Day 3, the normalized candidates: each with its state, qualification and
 * origin, its public sources, the qualification dimensions with their
 * reasons, and — while confirmation is awaited — confirm and reject.
 */

import type { ReactNode } from 'react';

import {
  decideAnalysisCompetitor,
  newIdempotencyKey,
  type AnalysisDay3State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, EmptyState, Tag } from '../../ui.js';
import { card } from './run-page-styles.js';
import { toneFor } from './run-state-labels.js';
import type { RunMutation } from './useAnalysisRunMutation.js';

export function CompetitorCandidateCards({
  id,
  run,
  day3,
  busy,
  mutate,
  competitorReason,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day3: AnalysisDay3State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly competitorReason: string;
}): ReactNode {
  return (
    <>
      {day3.candidates.length > 0 ? (
        <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
          {day3.candidates.map((candidate) => (
            <article key={candidate.businessId} style={card}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <h3 style={{ font: 'var(--type-heading-sm)' }}>
                  {candidate.displayName}
                </h3>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Tag tone={toneFor(candidate.state)}>{candidate.state}</Tag>
                  <Tag tone={toneFor(candidate.qualificationState ?? 'NEEDS_REVIEW')}>
                    {candidate.qualificationState ?? 'NEEDS_REVIEW'}
                  </Tag>
                  <Tag tone="info">{candidate.origin}</Tag>
                </div>
              </div>
              {candidate.sources.map((source) => (
                <p key={source.id}>
                  <a href={source.canonicalUrl} target="_blank" rel="noreferrer">
                    {source.platform}: {source.canonicalUrl}
                  </a>
                </p>
              ))}
              {candidate.qualification ? (
                <>
                  <div className="mj-form-two-column">
                    {Object.entries(candidate.qualification.dimensions).map(
                      ([name, dimension]) => (
                        <div
                          key={name}
                          style={{ ...card, background: 'var(--surface-subtle)' }}
                        >
                          <strong>{name.replaceAll(/([A-Z])/g, ' $1')}</strong>{' '}
                          <Tag tone={toneFor(dimension.state)}>{dimension.state}</Tag>
                          <p style={{ color: 'var(--text-secondary)' }}>
                            {dimension.reason}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                  <details>
                    <summary>Evidence, unknowns and provenance</summary>
                    <p>
                      Missing:{' '}
                      {candidate.qualification.missingInformation.join(', ') || 'None'}
                    </p>
                    <ul>
                      {candidate.qualification.reasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </details>
                </>
              ) : null}
              {run.currentStage === 'COMPETITOR_CONFIRMATION' &&
              run.executionState === 'AWAITING_REVIEW' ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginTop: 10,
                  }}
                >
                  <Button
                    size="sm"
                    variant="primary"
                    busy={busy === `confirm-${candidate.businessId}`}
                    disabled={
                      candidate.state === 'CONFIRMED' ||
                      candidate.qualificationState === 'REJECTED'
                    }
                    onClick={() =>
                      void mutate(`confirm-${candidate.businessId}`, () =>
                        decideAnalysisCompetitor(
                          id,
                          candidate.businessId,
                          'confirm',
                          newIdempotencyKey(),
                          competitorReason.trim() || undefined,
                        ),
                      )
                    }
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    busy={busy === `reject-${candidate.businessId}`}
                    disabled={
                      candidate.state === 'REJECTED' || !competitorReason.trim()
                    }
                    onClick={() =>
                      void mutate(`reject-${candidate.businessId}`, () =>
                        decideAnalysisCompetitor(
                          id,
                          candidate.businessId,
                          'reject',
                          newIdempotencyKey(),
                          competitorReason,
                        ),
                      )
                    }
                  >
                    Reject
                  </Button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : day3.targetScope !== null ? (
        <EmptyState title="No automated candidates. Add one manually." compact />
      ) : null}
    </>
  );
}
